import { useCallback, useMemo, useState } from "react";
import { Badge, Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import type { PlayerDetailResponse } from "../../shared/types";
import type { SetData } from "../util/setData";
import { usePlayerLookup } from "../hooks/usePlayerLookup";
import { SetList } from "./Sets";
import { SetInfo } from "./SetInfo";
import { StockIcons } from "./StockIcon";

interface FocusedPlayerProps {
  player: PlayerDetailResponse;
  onBack: () => void;
  onOpenTournament: (folder: string) => void;
  onOpenPlayer: (publicId: string) => void;
}

// A full-screen view dedicated to one canonical player, reached via its own
// URL (/player/<slug>). Unlike FocusedTournament's sets, these span multiple
// tournaments, so sets are grouped into a heading per tournament (SetList's
// groupBy="tournament") instead of being scoped to a single event.
export default function FocusedPlayer({
  player,
  onBack,
  onOpenTournament,
  onOpenPlayer,
}: FocusedPlayerProps) {
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  const sets: SetData[] = useMemo(
    () =>
      player.sets.map((s) => ({
        ...s.context,
        id: s.folder,
        games: s.games ?? [],
        gameSummaries: s.gameSummaries ?? [],
        folder: s.folder,
        tournamentFolder: s.tournamentFolder,
        tournamentSlug: s.tournamentSlug,
      })),
    [player.sets],
  );
  const selectedSet = sets.find((s) => s.id === selectedSetId) ?? null;

  const handleSelectSet = useCallback((id: string) => {
    setSelectedSetId((prev) => (prev === id ? null : id));
  }, []);

  // Which of the selected set's two entrant names already resolve to a
  // canonical player page, so SetInfo can make them clickable - resolution is
  // tournament-scoped (raw names aren't globally unique), so this is fetched
  // per selection rather than once for the whole cross-tournament history.
  const lookupNames = useMemo(
    () =>
      selectedSet
        ? Array.from(
            new Set(
              [...selectedSet.players.entrant1, ...selectedSet.players.entrant2].map((p) => p.name),
            ),
          )
        : [],
    [selectedSet],
  );
  const selectedSetPlayerLookup = usePlayerLookup(selectedSet?.tournamentFolder, lookupNames);

  const [copied, setCopied] = useState(false);
  const copyLink = useCallback(() => {
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  const tournamentCount = player.tournamentsPlayed.length;

  const setsCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">
        Sets
        <Badge bg="secondary" className="ms-2">
          {sets.length}
        </Badge>
      </h5>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {sets.length === 0 ? (
          <p className="text-muted py-3 text-center">No recorded sets</p>
        ) : (
          <SetList
            sets={sets}
            selectedSetId={selectedSetId}
            onSelectSet={handleSelectSet}
            onOpenTournament={onOpenTournament}
            groupBy="tournament"
          />
        )}
      </div>
    </div>
  );

  const setInfoCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">Set Info</h5>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {selectedSet ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              className="mb-2 d-lg-none"
              onClick={() => setSelectedSetId(null)}
            >
              <i className="bi bi-arrow-left me-1" />
              Back to Sets
            </Button>
            <SetInfo
              key={selectedSet.id}
              set={selectedSet}
              playerLookup={selectedSetPlayerLookup}
              onOpenPlayer={onOpenPlayer}
            />
          </>
        ) : (
          <p className="text-muted py-3 text-center">Select a set to view details</p>
        )}
      </div>
    </div>
  );

  return (
    <main className="flex-grow-1 overflow-hidden d-flex flex-column p-3 gap-3">
      <div className="tournament-header bg-body-secondary rounded px-3 py-2 position-relative d-flex flex-column align-items-center gap-2">
        <Button
          variant="outline-secondary"
          size="sm"
          className="tournament-header-back align-self-start flex-shrink-0"
          onClick={onBack}
        >
          <i className="bi bi-arrow-left me-1" />
          All tournaments
        </Button>

        <div className="text-center px-2">
          <h3 className="mb-1 text-truncate" title={player.entrant.displayName}>
            {player.entrant.displayName}
          </h3>
          <div className="text-body-secondary fs-5 mb-1">
            <span className="text-success">{player.wins}</span>
            <span className="text-muted">–</span>
            <span className="text-danger">{player.losses}</span>
            {" · "}
            {tournamentCount} {tournamentCount === 1 ? "tournament" : "tournaments"}
          </div>
          {player.characters.length > 0 && (
            <div>
              <StockIcons characters={player.characters} size={24} />
            </div>
          )}
        </div>

        <div className="tournament-header-actions d-flex flex-wrap gap-2 justify-content-center">
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id="copy-player-link-tooltip">Permanent link to this player's page</Tooltip>
            }
          >
            <Button
              variant="outline-secondary"
              size="sm"
              className="flex-shrink-0 d-inline-flex align-items-center gap-1"
              onClick={copyLink}
            >
              <i className={`bi ${copied ? "bi-check-lg" : "bi-link-45deg"}`} />
              {copied ? "Copied" : "Copy link"}
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      <Row className="flex-grow-1 g-3 overflow-hidden">
        <Col
          xs={12}
          lg={5}
          className={`h-100 overflow-hidden ${selectedSet ? "d-none d-lg-block" : ""}`}
        >
          {setsCard}
        </Col>
        <Col
          xs={12}
          lg={7}
          className={`h-100 overflow-hidden ${selectedSet ? "" : "d-none d-lg-block"}`}
        >
          {setInfoCard}
        </Col>
      </Row>
    </main>
  );
}
